import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import Menu from './pages/Menu';
import Orders from './pages/Orders';
import Delivery from './pages/Delivery';
import Allergeni from './pages/Allergeni';
import TenantManager from './pages/TenantManager';
import AdminImport from './pages/AdminImport';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { LocaleProvider } from './contexts/LocaleContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { PublicAuthProvider } from './contexts/PublicAuthContext';
import Navbar from './components/Navbar';
import MenuOverlay from './components/MenuOverlay';
import Events from './components/Events';
import './components/Events.css';
import TenantStatusBar from './components/TenantStatusBar';
import './styles/main.css';
import './App.css';
import './styles/tenant-theme.css';
import TenantThemeProvider from './components/TenantThemeProvider';
import { pdfjs } from 'react-pdf';
import { MenuProvider } from './contexts/MenuContext';
import { MenuTranslationProvider } from './contexts/MenuTranslationContext';
import { TranslatedTenantProvider } from './contexts/TranslatedTenantContext';
import MenuPage from './components/menu/MenuPage';
import PublicAuthModal from './components/auth/PublicAuthModal';

// Imposta il worker PDF.js - usa versione legacy compatibile
pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.legacy.js`;

function ProtectedOrders() {
    try {
        const { features } = useTenant();
        if (features && features.orderingEnabled) return <Orders />;
    } catch (_) { }
    return <Navigate to="/menu" replace />;
}

function ProtectedDelivery() {
    try {
        const { features } = useTenant();
        if (features && features.deliveryEnabled) return <Delivery />;
    } catch (_) { }
    return <Navigate to="/menu" replace />;
}

export default function App() {
    const [sezione, setSezione] = useState('home');
    const [menuOpen, setMenuOpen] = useState(false);
    const [eventsOpen, setEventsOpen] = useState(false);
    const [selectedCategoria, setSelectedCategoria] = useState(null);

    // Gestisce la classe overlay-open sul body per nascondere la navbar
    useEffect(() => {
        if (menuOpen || eventsOpen) {
            document.body.classList.add('overlay-open');
        } else {
            document.body.classList.remove('overlay-open');
        }

        // Cleanup when component unmounts
        return () => {
            document.body.classList.remove('overlay-open');
        };
    }, [menuOpen, eventsOpen]);

    // Sfoglia Menu: resetta la categoria per mostrare la selezione normale
    const handleSfogliaMenu = () => {
        // Se c'è già un overlay aperto, chiudilo prima
        if (menuOpen || eventsOpen) {
            setMenuOpen(false);
            setEventsOpen(false);
            setTimeout(() => {
                setSelectedCategoria(null); // <-- reset
                setMenuOpen(true);
            }, 100);
        } else {
            setSelectedCategoria(null); // <-- reset
            setMenuOpen(true);
        }
    };

    // Menu Feste: imposta direttamente la categoria
    const handleMenuFeste = () => {
        // Se c'è già un overlay aperto, chiudilo prima
        if (menuOpen || eventsOpen) {
            setMenuOpen(false);
            setEventsOpen(false);
            // Usa un breve timeout per permettere la chiusura prima di riaprire
            setTimeout(() => {
                setSelectedCategoria('menu-feste');
                setMenuOpen(true);
            }, 100);
        } else {
            setSelectedCategoria('menu-feste');
            setMenuOpen(true);
        }
    };

    // Eventi: apre la pagina Eventi dedicata
    const handleEventi = () => {
        // Se c'è già un overlay aperto, chiudilo prima
        if (menuOpen || eventsOpen) {
            setMenuOpen(false);
            setEventsOpen(false);
            setTimeout(() => {
                setEventsOpen(true);
            }, 100);
        } else {
            setEventsOpen(true);
        }
    };

    const handleAllergeni = () => {
        // Se c'è già un overlay aperto, chiudilo prima
        if (menuOpen || eventsOpen) {
            setMenuOpen(false);
            setEventsOpen(false);
            setTimeout(() => {
                setSelectedCategoria('allergeni');
                setMenuOpen(true);
            }, 100);
        } else {
            setSelectedCategoria('allergeni');
            setMenuOpen(true);
        }
    };

    const handleDeliveryNavigate = () => {
        if (menuOpen || eventsOpen) {
            setMenuOpen(false);
            setEventsOpen(false);
        }
        setSelectedCategoria(null);
    };

    // Ensure tenantId is present in URL if we have it in localStorage
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const hasTenantInUrl = params.has('tenantId');
        const stored = localStorage.getItem('tenantId');
        if (!hasTenantInUrl && stored) {
            try {
                params.set('tenantId', stored);
                const base = window.location.pathname.split('?')[0];
                const newUrl = `${base}?${params.toString()}`;
                window.history.replaceState({}, '', newUrl);
            } catch (_) { /* ignore */ }
        }
    }, []);

    return (
        <I18nextProvider i18n={i18n}>
            <LocaleProvider>
                <TenantProvider>
                    <PublicAuthProvider>
                        <TenantThemeProvider>
                            <Router>
                                <>
                                    <TenantStatusBar />
                                    <Navbar
                                        onSelectSection={setSezione}
                                        activeSection={sezione}
                                        onSfogliaMenu={handleSfogliaMenu}
                                        onMenuFeste={handleMenuFeste}
                                        onEventi={handleEventi}
                                        onDelivery={handleDeliveryNavigate}
                                    />
                                    {menuOpen && (
                                        <MenuOverlay
                                            className={menuOpen ? 'menu-overlay open' : 'menu-overlay'}
                                            open={menuOpen}
                                            selectedCategoria={selectedCategoria}
                                            setSelectedCategoria={setSelectedCategoria}
                                            onClose={() => setMenuOpen(false)}
                                        />
                                    )}
                                    {eventsOpen && (
                                        <Events
                                            open={eventsOpen}
                                            onClose={() => setEventsOpen(false)}
                                        />
                                    )}
                                    <MenuProvider>
                                        <TranslatedTenantProvider>
                                            <Routes>
                                                <Route path="/" element={<Home onAllergeni={handleAllergeni} />} />
                                                <Route path="/menu" element={<MenuPage />} />
                                                <Route path="/orders" element={<ProtectedOrders />} />
                                                <Route path="/delivery" element={<ProtectedDelivery />} />
                                                <Route path="/allergeni" element={<Allergeni />} />
                                                <Route path="/menu-page" element={<MenuPage />} />
                                                <Route path="/admin/tenants" element={<TenantManager />} />
                                                <Route path="/admin/import" element={<AdminImport />} />
                                            </Routes>
                                        </TranslatedTenantProvider>
                                    </MenuProvider>
                                    <PublicAuthModal />
                                </>
                            </Router>
                        </TenantThemeProvider>
                    </PublicAuthProvider>
                </TenantProvider>
            </LocaleProvider>
        </I18nextProvider>
    );
}